import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import format from "date-fns/format";
import { Dropdown, FilterComponent } from "@client/shared/components";
import { globalStyles, PAGE_KEYS } from "@client/shared/constants";
import {
  exportToFile,
  getUserData,
  queryStringBuilder,
  getPageConfig,
  setPageConfig,
} from "@client/utils";
import UsersView from "./view";
import Service from "../service";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

let gridHelper = null;

const defaultState = {
  entries: [],
  entriesCount: 0,
  isLoading: false,
  isExporting: false,
  pageNumber: 1,
  pageSize: 5,
  order: null,
  orderBy: "",
  filters: [],
  selectedRows: [],
};

const Users = () => {
  const pageConfig = getPageConfig(PAGE_KEYS.ADMIN_USER);

  const [state, setState] = useState({
    ...defaultState,
    pageSize: pageConfig?.pageSize || defaultState.pageSize,
  });
  const userData = (getUserData() || {}).user || {};

  const fetchEntries = useCallback(
    async (
      pageNumber = 1,
      pageSize = 5,
      order = "",
      orderBy = "",
      filters = []
    ) => {
      setState((prevState) => ({
        ...prevState,
        isLoading: true,
      }));
      const updatedFilters = filters.map((filter) => {
        const subFields = (
          filterConfig.find((item) => item.field === filter.field) || {}
        ).subFields;
        if (subFields) {
          filter.subFields = subFields;
        }
        return filter;
      });
      const queryString = queryStringBuilder(
        pageSize,
        (pageNumber - 1) * pageSize,
        [],
        updatedFilters,
        { field: orderBy, order }
      );
      const { data, error } = await Service.get(queryString);
      if (error) {
        setState((prevState) => ({
          ...prevState,
          isLoading: false,
        }));
        return toast.error(
          Array.isArray(error) ? error[0].message : error.message
        );
      }
      setState((prevState) => ({
        ...prevState,
        entries: data?.users || defaultState.entries,
        entriesCount: data?.count || defaultState.entriesCount,
        isLoading: false,
      }));
    },
    []
  );

  const patchUserPermissions = async (id, payload = {}, permissionType) => {
    setState((prevState) => ({
      ...prevState,
      rowBeingUpdated: {
        id,
        permissionType,
      },
    }));
    const { error } = await Service.patch(id, payload);
    if (error) {
      toast.error(
        Array.isArray(error) ? error[0].message : error.message || error
      );
    } else {
      toast.success("Updated successfully.");
      setState((prevState) => ({
        ...prevState,
        entries: prevState.entries.map((entry) => ({
          ...entry,
          ...(entry.rcRoleId === id ? payload : {}),
        })),
      }));
    }
    setState((prevState) => ({
      ...prevState,
      rowBeingUpdated: null,
    }));
  };

  const handlePageNumberChange = useCallback((pageNumber) => {
    setState((prevState) => ({
      ...prevState,
      pageNumber,
      selectedRows: defaultState.selectedRows,
    }));
    gridHelper && gridHelper.resetSelection();
  }, []);

  const handlePageSizeChange = useCallback((pageSize) => {
    setPageConfig(PAGE_KEYS.ADMIN_USER, {
      pageSize,
    });
    setState((prevState) => ({
      ...prevState,
      pageSize,
      pageNumber: defaultState.pageNumber,
      selectedRows: defaultState.selectedRows,
    }));
    gridHelper && gridHelper.resetSelection();
  }, []);

  const handleSortChange = useCallback((fieldObj, order) => {
    setState((prevState) => ({
      ...prevState,
      order,
      orderBy: order ? fieldObj.field || fieldObj.fieldName : null,
      selectedRows: defaultState.selectedRows,
    }));
    gridHelper && gridHelper.resetSelection();
  }, []);

  const handleFilterChange = useCallback((filters = []) => {
    setState((prevState) => ({
      ...prevState,
      filters,
    }));
  }, []);

  const handleSelectionChange = useCallback((selectedRows) => {
    setState((prevState) => ({
      ...prevState,
      selectedRows,
    }));
  }, []);

  const handleExport = useCallback(
    async (sortObj = null, filters = [], selectedRows = []) => {
      setState((prevState) => ({
        ...prevState,
        isExporting: true,
      }));
      let queryString = queryStringBuilder(0, 0, [], filters, sortObj);
      if (!!selectedRows.length) {
        queryString += `${queryString ? "&" : "?"}ids=${selectedRows.join(
          ","
        )}`;
      }
      const { data, error } = await Service.exportFile("Users", queryString);
      if (error) {
        toast.error(
          Array.isArray(error) ? error[0].message : error.message || error
        );
      } else {
        exportToFile(data, "admin_user_export");
      }
      setState((prevState) => ({
        ...prevState,
        isExporting: false,
      }));
    },
    []
  );

  const updateUserRole = async (role, id = null) => {
    setState((prevState) => ({
      ...prevState,
      isLoading: true,
    }));
    const { error } = await Service.updateRole(id, { role });

    if (error) {
      toast.error(
        Array.isArray(error) ? error[0].message : error.message || error
      );
    } else {
      toast.success("Role changed successfully.");
      fetchEntries(
        state.pageNumber,
        state.pageSize,
        state.order,
        state.orderBy,
        state.filters
      );
    }
    setState((prevState) => ({
      ...prevState,
      isLoading: false,
    }));
  };

  const filterConfig = [
    {
      field: "user",
      fieldToDisplay: "Name",
      operatorType: "string",
    },
    {
      field: "email",
      fieldToDisplay: "Email",
      operatorType: "string",
    },
    {
      field: "role",
      fieldToDisplay: "Role",
      operatorType: "number",
      options: [
        { label: "Admin", value: "Admin" },
        { label: "Customer", value: "Customer" },
        { label: "Partner", value: "Partner" },
        { label: "User", value: "User" },
      ],
      subFields: ["name"],
    },
    {
      field: "lastloginAt",
      fieldToDisplay: "Last Login",
      operatorType: "number",
      isDateField: true,
      hasTimeStamp: true,
    },
  ];

  const columnConfig = [
    {
      id: "firstName",
      label: "NAME",
      field: "firstName",
      canSort: true,
      noResize: true,
      render: (row) => {
        const name = row.firstName ? `${row.firstName} ${row?.lastName}` : "";
        return (
          <Tooltip title={name} placement="top-start">
            <Typography variant="body2" noWrap>
              {name || "-"}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      id: "email",
      field: "email",
      label: "EMAIL",
      hasEllipses: true,
      canSort: true,
      noResize: true,
    },
    {
      id: "lastloginAt",
      field: "lastloginAt",
      label: "LAST LOGIN",
      canSort: true,
      noResize: true,
      render: (row) => {
        const lastLogin = row.lastloginAt
          ? format(new Date(row.lastloginAt), "MM/dd/yyyy hh:mm aaa") || ""
          : "";
        return (
          <Tooltip placement="top-start" title={lastLogin}>
            <Typography variant="body2" noWrap>
              {lastLogin}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      id: "role",
      field: "role",
      label: "ROLE",
      noResize: true,
      render: (row) => {
        return (
          <Box>
            {userData.email === row.email ||
            row.role?.name === "Partner" ||
            row.role?.name === "Customer" ? (
              <Tooltip title={row.role?.name || ""} placement="top-start">
                <Typography variant="body2">{row.role?.name}</Typography>
              </Tooltip>
            ) : (
              <Dropdown
                customToggle={
                  <Box
                    sx={{
                      ...globalStyles.display.flex,
                      ...globalStyles.flex.align.center,
                      ...globalStyles.cursor.pointer,
                    }}
                  >
                    <Typography variant="body2" sx={{ mr: 1 }}>
                      {row.role?.name}
                    </Typography>
                    <KeyboardArrowDownIcon />
                  </Box>
                }
                options={[
                  {
                    label: "Admin",
                    onClick: () =>
                      row.role?.name !== "Admin" &&
                      updateUserRole("Admin", row.id),
                  },
                  {
                    label: "User",
                    onClick: () =>
                      row.role?.name !== "User" &&
                      updateUserRole("User", row.id),
                  },
                ]}
              />
            )}
          </Box>
        );
      },
    },
    {
      id: "client_name",
      field: "client_name",
      label: "CLIENT NAME",
      noResize: true,
      render: (row) => {
        return (
          <Tooltip title={row?.brandInfo?.name} placement="top-start" noWrap>
            <Typography variant="body2" noWrap>
              {row?.brandInfo?.name ? row?.brandInfo?.name : "-"}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      id: "col-action",
      label: "",
      renderHeader: () => (
        <Box
          sx={{
            ...globalStyles.display.flex,
            ...globalStyles.flex.justify.end,
          }}
        >
          <Tooltip title="Export" placement="top-start">
            <IconButton
              disabled={state.isExporting}
              sx={{ mr: 0 }}
              onClick={() => {
                if (!state.entries.length) {
                  return toast.error("No data to export");
                }
                handleExport(
                  { field: state.orderBy, order: state.order },
                  state.filters,
                  state.selectedRows
                );
              }}
            >
              <img src="/assets/export.svg" alt="export" />
            </IconButton>
          </Tooltip>
          <FilterComponent
            filterConfig={filterConfig}
            onFilterApply={handleFilterChange}
            appliedFilters={state.filters}
          />
        </Box>
      ),
    },
  ];

  useEffect(() => {
    fetchEntries(
      state.pageNumber,
      state.pageSize,
      state.order,
      state.orderBy,
      state.filters
    );
  }, [
    fetchEntries,
    state.pageNumber,
    state.pageSize,
    state.order,
    state.orderBy,
    state.filters,
  ]);

  return (
    <UsersView
      entries={state.entries}
      entriesCount={state.entriesCount}
      columnConfig={columnConfig}
      isLoading={state.isLoading}
      pageNumber={state.pageNumber}
      pageSize={state.pageSize}
      order={state.order}
      orderBy={state.orderBy}
      handlePageNumberChange={handlePageNumberChange}
      handlePageSizeChange={handlePageSizeChange}
      handleSortChange={handleSortChange}
      handleSelectionChange={handleSelectionChange}
      handleGridReady={(instance) => (gridHelper = instance)}
    />
  );
};

export default Users;
